import { useId, useCallback } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import { DevTool } from '@hookform/devtools'
import isBoolean from 'lodash/isBoolean'
import get from 'lodash/get'
import debounce from 'lodash/debounce'
import { usePub } from '@collegeboard-software/cm-consent-management-widget-package'
import {
  PrimaryButton,
  RadioButtonGroup,
  RadioButton,
  Spinner,
  Icon,
  Toasts,
  Toast,
  Spacer,
} from '@cb/apricot-react'
import { CBInputMethod } from '@cb/apricot'
import { isEmpty } from '@myap/ui-library'
import { STUDENT_RACE_OPTIONS } from '@myap/metadata'
import useUserSettingsQuery from '../../../hooks/useUserSettingsQuery'
import useStudentInfoAndRegistrationQuery from '../hooks/useStudentInfoAndRegistrationQuery'
import useStudentPrivacyQuery from '../hooks/useStudentPrivacyQuery'
import useStudentUpdateRegistrationMutation from '../hooks/useStudentUpdateRegistrationMutation'
import OpenNewWindow from '../../_common/actions/OpenNewWindow'
import FormErrorMsg from '../../_common/FormErrorMsg'
import {
  SchoolSearch,
  SchoolZipFilter,
  StudentGrade,
  StudentOrgId,
  StudentLanguage,
  StudentRaceEthnicity,
  ParentalEducation,
} from './fields'
import { getShadowRootNode } from '../../../utilities/dom'
import {
  DEFAULT_STUDENT_REGISTRATION_VALUES,
  FIELD_ZIP,
  FIELD_GRADE,
  FIELD_STUDENTID,
  FIELD_LANG,
  FIELD_PARENT1,
  FIELD_PARENT2,
  FIELD_STREET1,
  FIELD_STREET2,
  FIELD_STREET3,
  FIELD_CITY,
  FIELD_STATE,
  FIELD_POSTALCODE,
  FIELD_PROVINCE,
  FIELD_COUNTRY,
  FIELD_RACE,
  FIELD_ETHNICITY,
  FIELD_RECOGNIZED_TRIBE,
  FIELD_ENROLLED_TRIBE,
  FIELD_DESCENDANT_TRIBE,
  FIELD_SCHOOLNAME,
  FIELD_DISPLAY_SCHOOLNAME,
  FIELD_SCHOOLID,
  FIELD_ORG_CITY,
  FIELD_ORG_STATE,
  FIELD_ORG_COUNTRY,
  FIELD_EMAIL1,
  FIELD_GENDER,
  FIELD_NICKNAME,
  FIELD_DISPLAY_TRIBENAME,
  FIELD_ETHNICITY_CONSENTED,
  FIELD_MISSING_REG_INFO,
  ERROR_SERVER,
  ERROR_CONSENT,
  VALUE_DOMESTIC_COUNTRY,
  RE_COLLECTION_POINT,
  CONSENT_MAP_RE_KEY,
  CONSENT_OPT_IN,
} from './constants'

import styles from './profileandregform.module.scss'

function RegistrationForm({
  changeStep,
  nextStep,
  setOpen,
  withHeader = true,
  isDismissable = true,
  adjustHeight,
}) {
  const modalHeaderId = useId()
  const isStandAlone = typeof changeStep !== 'function'
  const [showToast, setShowToast] = useState(false)
  const [initialValues, setInitialValues] = useState(null)
  const [finishedInitializing, setFinishedInitializing] = useState(false)
  const [studentAddress, setStudentAddress] = useState({})
  const [attendingOrgInfo, setAttendingOrgInfo] = useState({})
  const [otherFields, setOtherFields] = useState({})
  const [consented, setConsented] = useState(false)
  const [disabled, setDisabled] = useState(true)

  const isDomestic = studentAddress[FIELD_COUNTRY] === VALUE_DOMESTIC_COUNTRY
  const pub = usePub()

  let toastTimerId = null

  // Get user details
  const {
    educationPeriod: {
      code: currentEPC,
      hasEnrollments: hasCurrentYearEnrollments,
      isRegistered: hasCurrentYearRegistration,
      descr: currentYearDescr,
    },
    educationPeriods,
  } = useUserSettingsQuery()

  const {
    code: previousEPC,
    hasEnrollments: hasPreviousYearEnrollments = false,
    isRegistered: hasPreviousYearRegistration = false,
    descr: previousYearDescr,
  } = educationPeriods.find(edpd => edpd.chronology === 'previous') ?? {}

  const academicYearDescriptions = {
    ...(hasCurrentYearRegistration && hasCurrentYearEnrollments
      ? { [currentEPC]: currentYearDescr }
      : {}),
    ...(hasPreviousYearRegistration && hasPreviousYearEnrollments
      ? { [previousEPC]: previousYearDescr }
      : {}),
  }

  const code =
    (hasCurrentYearRegistration && hasCurrentYearEnrollments) || !isStandAlone
      ? currentEPC
      : hasPreviousYearRegistration && hasPreviousYearEnrollments
      ? previousEPC
      : null
  const [selectedEPC, setSelectedEPC] = useState(code)
  const refresh = !hasCurrentYearEnrollments && !isStandAlone

  // RE: setting refresh prop to same as ProfileForm, if studentInfoAndRegistrationQuery is same
  // as request made on ProfileForm, data will be fetched from cache first and call to Appsync can
  // be avoided
  const variables = {
    code: selectedEPC,
    isStudent: true,
    refresh,
  }

  // Get student info and registration
  const { loading, data } = useStudentInfoAndRegistrationQuery({
    educationPeriod: selectedEPC,
    refresh,
  })

  // Mutate student registration
  const { mutate: update, loading: isSubmitting } = useStudentUpdateRegistrationMutation({
    educationPeriod: selectedEPC,
    refresh,
  })

  const debouncedResize = useCallback(
    debounce(() => {
      if (adjustHeight) {
        adjustHeight()
      }
    }, 250),
    []
  )

  useEffect(() => {
    window.addEventListener('resize', debouncedResize)
    return () => window.removeEventListener('resize', debouncedResize)
  }, [])

  useEffect(() => {
    CBInputMethod()
  }, [])

  useEffect(() => {
    let attendingOrgInfoObj = {}
    setFinishedInitializing(false)
    if (!loading && !isEmpty(data)) {
      const keys = data ? Object.keys(data) : []
      const initialVals = keys.reduce((obj, key) => {
        if (key !== '__typename') {
          if (key === 'address') {
            const addressData = data.address
            // is addressData an object?
            if (typeof addressData === 'object' && addressData !== null) {
              // get all the keys in address
              const addressKeys = Object.keys(addressData)
              setStudentAddress(
                addressKeys.reduce((obj, addrKey) => {
                  if (addrKey !== '__typename' && addrKey !== 'city') {
                    return { ...obj, [addrKey]: addressData[addrKey] || '' }
                  }
                  // We want to rename the city field so it doesn't clash with ORG CITY
                  if (addrKey === 'city') {
                    return { ...obj, [FIELD_CITY]: addressData.city || '' }
                  }
                  return obj
                }, {})
              )
            } else {
              // address was not an object, drop in default values
              setStudentAddress({
                // ...obj,
                [FIELD_STREET1]: '',
                [FIELD_STREET2]: '',
                [FIELD_STREET3]: '',
                [FIELD_CITY]: '',
                [FIELD_STATE]: '',
                [FIELD_POSTALCODE]: '',
                [FIELD_PROVINCE]: '',
                [FIELD_COUNTRY]: '',
              })
            }
          }
          if (key === 'attendingOrg') {
            const attendingOrgData = data.attendingOrg
            if (attendingOrgData !== null) {
              // get all the keys in attendingOrg
              const attendingOrgKeys = Object.keys(attendingOrgData)
              attendingOrgInfoObj = attendingOrgKeys.reduce((obj, orgKey) => {
                if (orgKey === 'orgName') {
                  return {
                    ...obj,
                    attendingOrgName: attendingOrgData.orgName || '',
                  }
                }
                if (orgKey === 'orgId') {
                  return { ...obj, attendingOrgId: attendingOrgData.orgId || '' }
                }
                // We want to rename the city, stateCd, countryCd fields so they don't clash with fields of same name
                if (orgKey === 'city') {
                  return { ...obj, [FIELD_ORG_CITY]: attendingOrgData.city || '' }
                }
                if (orgKey === 'stateCd') {
                  return { ...obj, [FIELD_ORG_STATE]: attendingOrgData.stateCd || '' }
                }
                if (orgKey === 'countryCd') {
                  return { ...obj, [FIELD_ORG_COUNTRY]: attendingOrgData.countryCd || '' }
                }
                return obj
              }, {})
            }
            // generate empty data when attendingOrg is null
            return {
              // ...obj,
              [FIELD_SCHOOLNAME]: '',
              [FIELD_SCHOOLID]: null,
              [FIELD_ORG_CITY]: '',
              [FIELD_ORG_STATE]: '',
              [FIELD_ORG_COUNTRY]: '',
            }
          }
          // if any remaining value is specifically null, convert to empty string
          // except for race and ethnicity, which cannot be returned as empty string
          return {
            ...obj,
            [key]:
              isBoolean(data[key]) ||
              data[key] !== null ||
              Array.isArray(data[key]) ||
              key === FIELD_ETHNICITY ||
              key === FIELD_RACE
                ? data[key]
                : '',
          }
        }
        return obj
      }, {})
      const finalInitializedValues = {
        [FIELD_DISPLAY_SCHOOLNAME]: `${attendingOrgInfoObj[FIELD_SCHOOLNAME]}${
          !isEmpty(attendingOrgInfoObj[FIELD_ORG_CITY]) ||
          !isEmpty(attendingOrgInfoObj[FIELD_ORG_STATE])
            ? ` (${
                !isEmpty(attendingOrgInfoObj[FIELD_ORG_CITY])
                  ? `${attendingOrgInfoObj[FIELD_ORG_CITY]}${
                      !isEmpty(attendingOrgInfoObj[FIELD_ORG_STATE]) ? ', ' : ''
                    }`
                  : ''
              }${attendingOrgInfoObj[FIELD_ORG_STATE]})`
            : ''
        }`,
        [FIELD_ZIP]: '',
        [FIELD_GRADE]: initialVals[FIELD_GRADE],
        [FIELD_STUDENTID]: initialVals[FIELD_STUDENTID],
        [FIELD_LANG]: initialVals[FIELD_LANG],
        [FIELD_PARENT1]: initialVals[FIELD_PARENT1],
        [FIELD_PARENT2]: initialVals[FIELD_PARENT2],
        [FIELD_DESCENDANT_TRIBE]: initialVals[FIELD_DESCENDANT_TRIBE],
        [FIELD_ENROLLED_TRIBE]: initialVals[FIELD_ENROLLED_TRIBE],
        [FIELD_RECOGNIZED_TRIBE]: initialVals[FIELD_RECOGNIZED_TRIBE],
        [FIELD_ETHNICITY]: initialVals[FIELD_ETHNICITY],
        [FIELD_RACE]: initialVals[FIELD_RACE],
        [FIELD_DISPLAY_TRIBENAME]: '',
      }
      setInitialValues(finalInitializedValues)
      setAttendingOrgInfo(attendingOrgInfoObj)
      setOtherFields({
        dob: initialVals.dob,
        [FIELD_EMAIL1]: initialVals[FIELD_EMAIL1],
        firstName: initialVals.firstName,
        lastName: initialVals.lastName,
        middleInitial: initialVals.middleInitial,
        [FIELD_GENDER]: initialVals[FIELD_GENDER],
        mobileNumber: initialVals.mobileNumber,
        mobileTextMsgAllowedInd: initialVals.mobileTextMsgAllowedInd,
        [FIELD_NICKNAME]: initialVals[FIELD_NICKNAME],
        studentId: initialVals.studentId,
      })
    }
  }, [data, loading])

  const staticFieldStyles = {
    label: {
      fontFamily: 'Roboto, sans-serif',
      fontSize: '16px',
      fontWeight: 400,
      color: '#1e1e1e',
      marginLeft: 0,
    },
    value: {
      fontFamily: 'Roboto, sans-serif',
      fontSize: '16px',
      fontWeight: 'bold',
      color: '#1e1e1e',
      marginLeft: 0,
    },
  }

  const methods = useForm({
    mode: 'onChange',
    disabled,
    defaultValues: DEFAULT_STUDENT_REGISTRATION_VALUES,
    values: initialValues,
  })

  const {
    handleSubmit,
    control,
    formState: { errors, isValid, isDirty },
    setError,
    trigger,
  } = methods

  // Get student privacy info
  // We ONLY show R&E during first time registration and only need data protection for
  // international students.
  const { loading: privacyDataLoading, data: privacyData } = useStudentPrivacyQuery({
    orgId: attendingOrgInfo?.attendingOrgId ? parseInt(attendingOrgInfo.attendingOrgId, 10) : null,
    countryCd: studentAddress[FIELD_COUNTRY],
    otherSkipConditions: isStandAlone || isDomestic,
  })

  useEffect(() => {
    if (initialValues !== null) {
      // The whole purpose for this is to not render the form till all form values have been
      // initialized. Mostly so the school search input will populate with the default value.
      setFinishedInitializing(true)
    }
  }, [initialValues])

  useEffect(() => {
    setDisabled(loading || privacyDataLoading || isSubmitting)
  }, [loading, privacyDataLoading, isSubmitting])

  useEffect(() => {
    if (!loading && !privacyDataLoading && finishedInitializing && adjustHeight) {
      adjustHeight()
    }
  }, [
    loading,
    privacyDataLoading,
    finishedInitializing,
    adjustHeight,
    errors?.root?.serverError?.type,
    selectedEPC,
    attendingOrgInfo?.attendingOrgId,
  ])

  useEffect(() => {
    if (!isEmpty(get(errors, `${ERROR_SERVER}.message`))) {
      const errorEl = getShadowRootNode().querySelector('.form-error')
      errorEl?.scrollIntoView(true)
      errorEl?.focus()
    }
  }, [get(errors, `${ERROR_SERVER}.message`)])

  useEffect(() => {
    trigger(FIELD_DISPLAY_SCHOOLNAME)
  }, [attendingOrgInfo.attendingOrgId])

  const onSubmit = async values => {
    if (isSubmitting) {
      return
    }
    try {
      const {
        [FIELD_ZIP]: zip, // remove
        [FIELD_RECOGNIZED_TRIBE]: enrolledRecognizedTribeInd,
        [FIELD_ENROLLED_TRIBE]: enrolledTribeCd,
        [FIELD_DESCENDANT_TRIBE]: descendantTribe,
        [FIELD_DISPLAY_SCHOOLNAME]: displaySchoolName, // remove
        [FIELD_RACE]: raceCodes,
        [FIELD_ETHNICITY]: hispanicOriginCodes,
        [FIELD_DISPLAY_TRIBENAME]: tribeDisplayName, // remove
        [FIELD_MISSING_REG_INFO]: missingRegInfoInd, // remove
        resetOrgInd, // remove
        __typename,
        ...saveable
      } = values // filter out UI only fields and Native American tribe fields
      // We don't want to include Native American tribe fields if raceCode 1 is not selected
      const tribeFields =
        Array.isArray(values[FIELD_RACE]) &&
        values[FIELD_RACE].includes(STUDENT_RACE_OPTIONS.nativeAmerican.value)
          ? {
              enrolledRecognizedTribeInd,
              enrolledTribeCd: enrolledTribeCd !== '' ? enrolledTribeCd : null,
              descendantTribe,
            }
          : {}
      const fieldsForProcessing = { ...saveable, ...otherFields }
      // reprocess all values with an empty string as null so service doesn't complain
      const processedSaveable = Object.keys(fieldsForProcessing).reduce((acc, key) => {
        if (fieldsForProcessing[key] === '') {
          return { ...acc, [key]: null }
        }
        return { ...acc, [key]: fieldsForProcessing[key] }
      }, {})
      const updateObj = {
        ...processedSaveable,
        ...(!hasCurrentYearRegistration
          ? {
              [FIELD_ETHNICITY_CONSENTED]: consented,
              [FIELD_RACE]: raceCodes,
              [FIELD_ETHNICITY]: hispanicOriginCodes,
              ...tribeFields,
            }
          : {}),
        address: {
          city: studentAddress[FIELD_CITY],
          [FIELD_COUNTRY]: studentAddress[FIELD_COUNTRY],
          [FIELD_POSTALCODE]: studentAddress[FIELD_POSTALCODE],
          [FIELD_PROVINCE]: studentAddress[FIELD_PROVINCE],
          [FIELD_STATE]: studentAddress[FIELD_STATE],
          [FIELD_STREET1]: studentAddress[FIELD_STREET1],
          [FIELD_STREET2]: studentAddress[FIELD_STREET2],
          [FIELD_STREET3]: studentAddress[FIELD_STREET3],
        },
        attendingOrg: {
          orgName: attendingOrgInfo[FIELD_SCHOOLNAME],
          orgId: attendingOrgInfo[FIELD_SCHOOLID],
          city: attendingOrgInfo[FIELD_ORG_CITY],
          stateCd: attendingOrgInfo[FIELD_ORG_STATE],
          countryCd: attendingOrgInfo[FIELD_ORG_COUNTRY],
        },
        attendingOrgId: attendingOrgInfo[FIELD_SCHOOLID],
        attendingOrgName: attendingOrgInfo[FIELD_SCHOOLNAME],
      }
      delete variables.refresh
      await update({
        variables: {
          ...variables,
          input: updateObj,
          isRegistration: true,
          isStudent: true,
        },
      })
      if (isStandAlone && !isDismissable) {
        setOpen(false)
      } else if (isStandAlone) {
        setShowToast(true)
        toastTimerId = setTimeout(() => setShowToast(false), 5000)
      } else {
        changeStep(nextStep)
      }
    } catch (err) {
      setError(ERROR_SERVER, {
        type: 'Server Error',
        message: 'Error saving registration information. Please try again later.',
      })
    }
  }

  const publishConsent = values =>
    pub('consent-management-submit-decisions', {
      payload: {
        collectionPoint: RE_COLLECTION_POINT,
        consents: new Map([[CONSENT_MAP_RE_KEY, CONSENT_OPT_IN]]),
        callback: ({ error }) => {
          if (error) {
            setError(ERROR_CONSENT, {
              type: 'Server Error',
              message: `Consent Submission Error: ${error}`,
            })
            return
          }
          onSubmit(values)
        },
      },
    })

  if (loading || !finishedInitializing) {
    return (
      <div id="modalContent" className="cb-modal-content">
        <Spinner spinnerId="profileFormSpinner" size="64" />
      </div>
    )
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(consented ? publishConsent : onSubmit)}>
        {withHeader ? (
          <div className="cb-modal-header cb-modal-has-close">
            <h2 className="cb-modal-title" id={`apricot_modal_header_${modalHeaderId}`}>
              {isStandAlone ? 'AP Registration' : 'AP Registration - Step 2'}
            </h2>
            {isDismissable ? (
              <button
                type="button"
                className="cb-btn cb-btn-square cb-btn-greyscale cb-btn-close"
                aria-describedby={`apricot_modal_header_${modalHeaderId}`}
                data-cb-modal-close="true"
              >
                <span />
                <Icon name="x-mark" />
                <span className="sr-only">Close Modal</span>
              </button>
            ) : null}
          </div>
        ) : null}
        <div id="modalContent" className="cb-modal-content">
          <div className={styles.registration}>
            <Toasts bottom>
              <Toast
                timeout={5000}
                type="success"
                message="AP Registration saved"
                controlled={true}
                show={showToast}
                onDismiss={() => {
                  clearTimeout(toastTimerId)
                  setShowToast(false)
                }}
              />
            </Toasts>
            {isStandAlone ? (
              <p>
                Please confirm the information below. If you update the school you attend,
                you&#39;ll also need to go to your{' '}
                <OpenNewWindow url="https://my.collegeboard.org/profile/information">
                  College Board account settings
                </OpenNewWindow>{' '}
                to make the same change &mdash; changes made to your AP Profile do not automatically
                carry over to your College Board account. <strong>NOTE:</strong> Be careful when
                selecting your school as there could be multiple search results of the same name.
                The city and state are listed next to the school name; you can also filter by ZIP
                code.
              </p>
            ) : (
              <p>
                Please confirm the information below. If you update the school you attend,
                you&#39;ll also need to go to your{' '}
                <OpenNewWindow url="https://my.collegeboard.org/profile/information">
                  College Board account settings
                </OpenNewWindow>{' '}
                to make the same change &mdash; changes made to your AP Profile do not automatically
                carry over to your College Board account. <strong>NOTE:</strong> Be careful when
                selecting your school as there could be multiple search results of the same name.
                The city and state are listed next to the school name; you can also filter by ZIP
                code.
              </p>
            )}
            <Spacer size="24" />
            {isStandAlone && !data?.[FIELD_MISSING_REG_INFO] ? (
              <div style={{ display: 'inline-block' }}>
                {hasCurrentYearRegistration &&
                hasCurrentYearEnrollments &&
                hasPreviousYearRegistration &&
                hasPreviousYearRegistration &&
                !data?.[FIELD_MISSING_REG_INFO] ? (
                  <RadioButtonGroup
                    name="yearRadioGroup"
                    legend="School Year"
                    defaultValue={selectedEPC}
                    onChange={value => setSelectedEPC(parseInt(value, 10))}
                  >
                    <RadioButton
                      id="currentYearRadio"
                      label={currentYearDescr}
                      value={currentEPC}
                      disabled={disabled}
                    />
                    <RadioButton
                      id="previousYearRadio"
                      label={previousYearDescr}
                      value={previousEPC}
                      disabled={disabled}
                    />
                  </RadioButtonGroup>
                ) : (
                  <>
                    <div className="cb-margin-top-24" style={staticFieldStyles.label}>
                      School Year
                    </div>
                    <div style={staticFieldStyles.value}>
                      {academicYearDescriptions[selectedEPC]}
                    </div>
                  </>
                )}
              </div>
            ) : null}
            <div style={{ display: 'inline-block' }}>
              <Spinner
                show={loading}
                size="16"
                className={
                  isStandAlone && !data?.[FIELD_MISSING_REG_INFO]
                    ? 'cb-margin-left-24'
                    : 'cb-margin-top-16'
                }
              />
            </div>
            {isStandAlone && !data?.[FIELD_MISSING_REG_INFO] && data.studentId ? (
              <>
                <div className="cb-spacerv-24" />
                <div style={staticFieldStyles.label}>AP ID</div>
                <div style={staticFieldStyles.value}>{data.studentId}</div>
              </>
            ) : null}
            <Spacer size="24" />
            {finishedInitializing ? (
              <SchoolSearch
                disabled={disabled}
                attendingOrgInfo={attendingOrgInfo}
                setAttendingOrgInfo={setAttendingOrgInfo}
                studentAddress={studentAddress}
                currentEPC={currentEPC}
                selectedEPC={selectedEPC}
              />
            ) : null}
            <Spacer size="24" />
            <SchoolZipFilter disabled={disabled} studentCountry={studentAddress?.[FIELD_COUNTRY]} />
            {(hasCurrentYearRegistration &&
              hasCurrentYearEnrollments &&
              selectedEPC === currentEPC) ||
            !isStandAlone ? (
              <>
                <Spacer size="24" />
                <StudentGrade disabled={disabled} />
                <Spacer size="24" />
                <StudentOrgId disabled={disabled} />
                <p className="cb-font-size-small">
                  If you have a student identifier or student ID number issued by your state,
                  district, or school enter it here. You may enter up to 25 characters. Do not enter
                  your social security number in this field.
                </p>
                <Spacer size="24" />
                <StudentLanguage disabled={disabled} />
                {!hasCurrentYearRegistration && !privacyData?.scrub ? (
                  <>
                    <Spacer size="24" />
                    <StudentRaceEthnicity
                      disabled={disabled}
                      stateCd={studentAddress.stateCd}
                      personId={data?.personId}
                      setConsented={setConsented}
                      consented={consented}
                    />
                  </>
                ) : null}
                <Spacer size="24" />
                <ParentalEducation disabled={disabled} />
                <Spacer size="24" />
              </>
            ) : null}
            {!isEmpty(get(errors, `${ERROR_SERVER}.message`)) ? (
              <FormErrorMsg error={get(errors, `${ERROR_SERVER}.message`)} id="serverError" />
            ) : null}
          </div>
        </div>
        <div className="cb-modal-footer">
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <p className="cb-margin-bottom-16">
              Before clicking <strong>Save</strong>, confirm that all information entered on this
              form is accurate.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <PrimaryButton
                type="submit"
                disabled={!isValid || isSubmitting || (!isDirty && isStandAlone)}
                loading={isSubmitting}
              >
                Save
              </PrimaryButton>
            </div>
          </div>
        </div>
        {globalThis?.__DEV__ ? <DevTool control={control} /> : null}
      </form>
    </FormProvider>
  )
}

export default RegistrationForm
